import { Radio, RadioGroup } from '@headlessui/react';
import { twJoin } from 'tailwind-merge';

export interface RadioOption {
  name: string;
  description: React.ReactNode;
  disabled?: boolean;
}

type Props = {
  options?: RadioOption[];
  selected: RadioOption | undefined;
  onChange: (option: RadioOption) => void;
  disabled?: boolean;
};

export function RadioOptions({
  options = [],
  selected,
  onChange,
  disabled = false,
}: Props): React.ReactElement {
  return (
    <fieldset>
      <RadioGroup
        value={selected || ''}
        onChange={onChange}
        className="font-aribau -space-y-[2px]"
        disabled={disabled}
      >
        {options.map((option) => (
          <Radio
            key={option.name}
            value={option}
            aria-label={option.name}
            aria-description={option.description?.toString()}
            disabled={option.disabled}
            aria-disabled={option.disabled}
            className={twJoin(
              'group relative flex cursor-pointer border-2 border-gray-200 p-4 focus:outline-none',
              'data-[checked]:z-10 data-[checked]:border-primary-600 data-[checked]:bg-primary-50',
              'data-[disabled]:opacity-30 data-[disabled]:cursor-not-allowed',
              disabled && 'cursor-not-allowed',
            )}
          >
            <span
              aria-hidden="true"
              className={twJoin(
                'mt-0.5 flex h-4 w-4 shrink-0 cursor-pointer items-center justify-center',
                'rounded-full border border-gray-300 bg-white',
                'group-data-[checked]:border-transparent group-data-[checked]:bg-primary-600',
                'group-data-[focus]:ring-2 group-data-[focus]:ring-primary-600',
                'group-data-[focus]:ring-offset-1',
              )}
            >
              <span className="h-1.5 w-1.5 rounded-full bg-white" />
            </span>
            <span className="ml-3 flex flex-col">
              <span className="block text-sm font-aribaubold text-gray-500 group-data-[checked]:text-primary-600">
                {option.name}
              </span>
              <span className="block text-sm text-gray-500 group-data-[checked]:text-primary-600">
                {option.description}
              </span>
            </span>
          </Radio>
        ))}
      </RadioGroup>
    </fieldset>
  );
}
